import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Upload, Trash2, LogOut, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const fetchImages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/gallery`);
      setImages(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!authLoading && (!user || user === false)) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      setPreview(URL.createObjectURL(f));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      formData.append('description', description);

      await axios.post(`${API}/api/gallery`, formData, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      setTitle('');
      setDescription('');
      setFile(null);
      setPreview(null);
      fetchImages();
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/api/gallery/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      fetchImages();
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (authLoading) {
    return <div className="min-h-[70vh] flex items-center justify-center"><p>Loading...</p></div>;
  }

  return (
    <main className="py-12 md:py-20">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <h1 data-testid="admin-dashboard-heading" className="font-heading text-2xl md:text-3xl text-[#3D3D3D]">
              Gallery Manager
            </h1>
            <p className="text-gray-500 text-sm mt-1">Upload and manage project photos</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            data-testid="admin-logout-button"
            className="flex items-center gap-2"
          >
            <LogOut size={16} />
            Logout
          </Button>
        </div>

        {/* Upload Form */}
        <div className="bg-white border border-gray-200 rounded-lg p-8 mb-12">
          <h2 className="font-heading text-lg text-[#3D3D3D] mb-6 flex items-center gap-2">
            <Upload size={20} className="text-[#7AC943]" />
            Upload New Image
          </h2>
          <form onSubmit={handleUpload} data-testid="upload-form" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title (optional)</Label>
                <Input
                  id="title"
                  data-testid="upload-title-input"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="e.g., LED Screen Installation"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Input
                  id="description"
                  data-testid="upload-description-input"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Brief description"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="file">Image File</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="file"
                  type="file"
                  data-testid="upload-file-input"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="flex-1"
                />
                <Button
                  type="submit"
                  data-testid="upload-submit-button"
                  disabled={!file || uploading}
                  className="bg-[#7AC943] text-black hover:bg-[#68B038] font-semibold"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </div>

            {preview && (
              <div className="mt-4">
                <img src={preview} alt="Preview" className="h-32 w-auto rounded-lg border border-gray-200" />
              </div>
            )}
          </form>
        </div>

        {/* Gallery Grid */}
        <div>
          <h2 className="font-heading text-lg text-[#3D3D3D] mb-6 flex items-center gap-2">
            <ImageIcon size={20} className="text-[#7AC943]" />
            Gallery Images ({images.length})
          </h2>

          {images.length === 0 ? (
            <div data-testid="empty-gallery-message" className="text-center py-16 border border-dashed border-gray-300 rounded-lg">
              <ImageIcon size={40} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-400 text-sm">No images uploaded yet. Upload your first project photo above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((img) => (
                <div key={img.id} data-testid={`admin-image-${img.id}`} className="group relative bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={img.url.startsWith('http') ? img.url : `${API}${img.url}`}
                      alt={img.title || 'Gallery image'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#3D3D3D]">{img.title || 'Untitled'}</p>
                      {img.description && <p className="text-xs text-gray-400 mt-1">{img.description}</p>}
                    </div>
                    <button
                      onClick={() => handleDelete(img.id)}
                      data-testid={`delete-image-${img.id}`}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
